export interface TypingAnimatorConfig {
  minUpdateInterval: number; // e.g., 20ms
  maxUpdateInterval: number; // e.g., 50ms
  minCharsPerUpdate: number; // e.g., 1
  maxCharsPerUpdate: number; // e.g., 3
}

export class TypingAnimator {
  private readonly config: TypingAnimatorConfig;
  private readonly onUpdate: (text: string) => void;
  private readonly onComplete: () => void;
  private queue: string[] = [];
  private isRunning = false;
  private timer: NodeJS.Timeout | null = null;
  private fullText = '';

  constructor(
    onUpdate: (text: string) => void,
    onComplete: () => void,
    config?: Partial<TypingAnimatorConfig>,
  ) {
    this.onUpdate = onUpdate;
    this.onComplete = onComplete;
    this.config = {
      minUpdateInterval: 100,
      maxUpdateInterval: 200,
      minCharsPerUpdate: 10,
      maxCharsPerUpdate: 30,
      ...config,
    };
  }

  private isStreamFinished = false;

  public add(textChunk: string): void {
    if (this.isStreamFinished) return;

    const wasEmpty = this.queue.length === 0;
    this.queue.push(...textChunk.split(''));

    if (!this.isRunning) {
      this.start();
    } else if (wasEmpty && this.queue.length > 0) {
      this.scheduleNextUpdate();
    }
  }

  public finish(): void {
    this.isStreamFinished = true;
    if (!this.isRunning) {
      this.onComplete();
    }
  }

  public forceStop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.isRunning = false;
  }

  private start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.scheduleNextUpdate();
  }

  private scheduleNextUpdate(): void {
    if (!this.isRunning) return;

    if (this.queue.length === 0) {
      if (this.isStreamFinished) {
        this.isRunning = false;
        this.onComplete();
      }
      return;
    }

    const delay = this.config.minUpdateInterval
      + Math.random() * (this.config.maxUpdateInterval - this.config.minUpdateInterval);
    this.timer = setTimeout(() => this.performUpdate(), delay);
  }

  private performUpdate(): void {
    const { minCharsPerUpdate, maxCharsPerUpdate } = this.config;
    const charCount = minCharsPerUpdate
      + Math.floor(Math.random() * (maxCharsPerUpdate - minCharsPerUpdate + 1));
    const chars = this.queue.splice(0, charCount).join('');
    this.fullText += chars;

    this.onUpdate(this.fullText);
    this.scheduleNextUpdate();
  }
}
